import * as React from "react";
import cn from "classnames";
import { SortEnd } from "react-sortable-hoc";
import { Icon, IconName } from "../Icon";
import {
    Disclosure,
    DisclosureButton,
    DisclosurePanel,
    useDisclosureContext,
} from "../Disclosure";
import { ProgressBar } from "../ProgressBar";
import {
    SortableContainer,
    SortableHandle,
    SortableElement,
} from "../Sortable";
import {
    calculateRankProgress,
    isCourseCompleted,
} from "./utils";
import { default as LockedIcon } from "./locked-icon.svg";
import styles from "./styles.module.scss";
import { IEnrollmentComponentCourseView, IEnrollmentComponentRankView } from "./types";

interface RankControl {
    icon: IconName;
    name: string;
    onClick: (rankIndex: number) => void;
}

export type RenderCourse<C extends IEnrollmentComponentCourseView, R extends IEnrollmentComponentRankView<C>> = (
    course: C,
    { rank, rankIndex, courseIndex }: IRenderCourseParams<R>,
) => React.ReactNode;

export interface IRenderCourseParams<T> {
    courseIndex: number;
    rank: T;
    rankIndex: number;
}

interface IProps<C extends IEnrollmentComponentCourseView, R extends IEnrollmentComponentRankView<C>> {
    children?: React.ReactNode;
    displayRanks: boolean;
    displayRankImages: boolean;
    ranks: R[];
    renderCourse: RenderCourse<C, R>;
    withProgress?: boolean;
    openRankIds?: string[];
    openRankIndexes?: number[];
    sortableRanks?: boolean;
    disalableRanks?: boolean;
    sortableCourses?: boolean;
    rankControls?: RankControl[];
    coursesWrapperClassName?: string;
    rankImageClassname?: string;
    backgroundColorTransition?: boolean;
    newCourseComponent?: ({
        rankIndex,
    }: {
        rankIndex: number;
    }) => React.ReactNode;
    onCourseMove?: (
        parentRankIndex: number,
        oldIndex: number,
        newIndex: number,
    ) => void;
}

interface IEnrollmentComponentRankProps<C extends IEnrollmentComponentCourseView, R extends IEnrollmentComponentRankView<C>> {
    rank: R;
    rankIndex: number;
    displayRanks: boolean;
    displayRankImages: boolean;
    renderCourse: RenderCourse<C, R>;
    withProgress?: boolean;
    sortableRanks?: boolean;
    disalableRanks?: boolean;
    sortableCourses?: boolean;
    rankControls?: RankControl[];
    coursesWrapperClassName?: string;
    backgroundColorTransition?: boolean;
    rankImageClassname?: string;
    newCourseComponent?: ({
        rankIndex,
    }: {
        rankIndex: number;
    }) => React.ReactNode;
    onCourseMove?: (
        parentRankIndex: number,
        oldIndex: number,
        newIndex: number,
    ) => void;
}

function hasHorizontalScroll(e?: HTMLElement | null) {
    if (!e) {
        return false;
    }
    return e.scrollWidth > e.clientWidth;
}

function EnrollmentRank<C extends IEnrollmentComponentCourseView, R extends IEnrollmentComponentRankView<C>>({
    rank,
    rankIndex,
    displayRanks,
    displayRankImages,
    renderCourse,
    withProgress,
    sortableRanks,
    disalableRanks,
    rankControls,
    newCourseComponent,
    onCourseMove,
    sortableCourses,
    coursesWrapperClassName,
    rankImageClassname,
    backgroundColorTransition = true,
}: IEnrollmentComponentRankProps<C, R>) {
    const [iconError, setIconError] = React.useState<boolean>(false);
    const { open: isRankOpen } = useDisclosureContext();

    const rankProgress = calculateRankProgress(rank);

    const coursesCompleted = (rank.courses as any[]).filter(isCourseCompleted)
        .length;

    const isRankDisabled =
        disalableRanks &&
        !rank.courses?.some((c: IEnrollmentComponentCourseView) =>
            !!c.enrollment?.enrollmentId);

    const coursesWrapper = React.useRef<HTMLDivElement | null>(null);
    const [
        coursesWrapperHasScrollbar,
        setCoursesWrapperHasScrollbar,
    ] = React.useState(false);

    const onIconError = () => {
        setIconError(true);
    };

    const scrollLeft = () => {
        if (!coursesWrapper.current) {
            return;
        }

        const courseWidth =
            coursesWrapper.current!.querySelector("div")?.clientWidth ?? 0;

        coursesWrapper.current.scrollBy(-courseWidth, 0);
    };

    const scrollRight = () => {
        if (!coursesWrapper.current) {
            return;
        }

        const courseWidth =
            coursesWrapper.current!.querySelector("div")?.clientWidth ?? 0;

        coursesWrapper.current.scrollBy(courseWidth, 0);
    };

    const courses = (
        <div
            className={cn(styles.coursesWrapper, coursesWrapperClassName)}
            ref={coursesWrapper}
        >
            {rank.courses?.map((c, courseIndex) =>
                renderCourse(c, { rank, rankIndex, courseIndex }),
            )}

            {newCourseComponent ? newCourseComponent({ rankIndex }) : null}
        </div>
    );

    React.useEffect(() => {
        if (!isRankOpen) {
            setCoursesWrapperHasScrollbar(false);
        }

        setCoursesWrapperHasScrollbar(
            hasHorizontalScroll(coursesWrapper.current),
        );
    }, [isRankOpen, rank]);

    const renderEnrollmentIcon = () => {
        const icon = rank.iconUrl ?? rank.icon;
        if (icon) {
            if (iconError) {
                return (
                    <Icon name="plus" className={cn(styles.placeholderIcon)} />
                );
            } else {
                return (
                    <img
                        alt={rank.title}
                        className={cn(styles.rankImage, rankImageClassname)}
                        src={icon}
                        onError={onIconError}
                    />
                );
            }
        } else {
            return <Icon name="plus" className={cn(styles.placeholderIcon)} />;
        }
    };

    return (
        <div
            className={cn({
                [styles.rank]: true,
                [styles.rankOpen]: isRankOpen,
            })}
        >
            <div className={styles.rankControls}>
                {(rankControls || []).map((rankControl, rankControlIndex) => (
                    <button
                        key={rankControlIndex}
                        type="button"
                        className={styles.rankControl}
                        onClick={() => {
                            rankControl.onClick(rankIndex);
                        }}
                    >
                        <Icon
                            name={rankControl.icon}
                            className={styles.rankControlIcon}
                        />
                        {rankControl.name}
                    </button>
                ))}

                {sortableRanks ? (
                    <SortableHandle>
                        <div className={styles.reorderControl}>
                            <Icon
                                name="arrows-alt"
                                className={styles.reorderIcon}
                            />
                        </div>
                    </SortableHandle>
                ) : null}
            </div>

            <DisclosureButton
                className={cn({
                    [styles.rankToggle]: true,
                })}
            >
                <div className={styles.rankToggleContent}>
                    {displayRanks && displayRankImages && (
                        <div
                            className={cn({
                                [styles.rankIcon]: true,
                                [styles.rankIconDisabled]: isRankDisabled,
                            })}
                        >
                            {isRankDisabled ? (
                                <LockedIcon />
                            ) : (
                                renderEnrollmentIcon()
                            )}
                        </div>
                    )}

                    <h4
                        className={cn({
                            [styles.rankTitle]: true,
                            [styles.rankTitlePadded]:
                                displayRanks && displayRankImages,
                        })}
                    >
                        {displayRanks && rank.title}
                        <Icon
                            name="angle-down"
                            className={styles.rankTitleToggleIcon}
                        />
                    </h4>

                    {withProgress ? (
                        <div className={styles.rankProgress}>
                            <ProgressBar
                                value={rankProgress}
                                className={styles.rankProgressBar}
                            />

                            <span className={styles.rankProgressText}>
                                {coursesCompleted}{" "}
                                <span
                                    className={styles.rankProgressTextSeparator}
                                >
                                    /
                                </span>{" "}
                                {rank.courses?.length ?? 0}
                            </span>
                        </div>
                    ) : null}
                </div>
            </DisclosureButton>

            <DisclosurePanel>
                <div
                    className={cn({
                        [styles.rankCourses]: true,
                        [styles.rankCoursesWithBackgroundColorTransition]: backgroundColorTransition,
                        [styles.rankCoursesWithControls]: coursesWrapperHasScrollbar,
                    })}
                >
                    {coursesWrapperHasScrollbar && (
                        <button
                            type="button"
                            className={cn(
                                styles.scrollControl,
                                styles.scrollControlLeft,
                            )}
                            onClick={() => scrollLeft()}
                        >
                            <Icon name="chevron-left" />
                        </button>
                    )}

                    {sortableCourses ? (
                        <SortableContainer
                            onSortEnd={({ oldIndex, newIndex }: SortEnd) => {
                                onCourseMove?.(rankIndex, oldIndex, newIndex);
                            }}
                            axis="x"
                            distance={1}
                            as={React.Fragment}
                        >
                            {courses}
                        </SortableContainer>
                    ) : (
                        courses
                    )}

                    {coursesWrapperHasScrollbar && (
                        <button
                            type="button"
                            className={cn(
                                styles.scrollControl,
                                styles.scrollControlRight,
                            )}
                            onClick={() => scrollRight()}
                        >
                            <Icon name="chevron-right" />
                        </button>
                    )}
                </div>
            </DisclosurePanel>
        </div>
    );
}

export function Enrollment<C extends IEnrollmentComponentCourseView, R extends IEnrollmentComponentRankView<C>>({
    displayRanks,
    displayRankImages,
    ranks,
    renderCourse,
    withProgress = true,
    openRankIds = [],
    openRankIndexes = [],
    sortableRanks = false,
    disalableRanks = true,
    sortableCourses = false,
    rankControls,
    newCourseComponent,
    onCourseMove,
    coursesWrapperClassName,
    rankImageClassname,
    backgroundColorTransition = true,
}: IProps<C, R>) {
    return (
        <div>
            <div>
                {ranks.map((rank, rankIndex) => {
                    const rankId = rank.rankId ?? rank.tempId ?? rank.id ?? rankIndex.toString();
                    const rankElement = (
                        <div
                            className={cn({
                                [styles.rankRow]: true,
                                [styles.rankRowPadded]:
                                    displayRanks && displayRankImages,
                            })}
                            key={rankId}
                            data-testid="rank-item"
                        >
                            <Disclosure
                                open={
                                    openRankIds?.includes(rankId) ||
                                    openRankIndexes.includes(rankIndex)
                                }
                            >
                                <EnrollmentRank
                                    rank={rank}
                                    rankIndex={rankIndex}
                                    displayRanks={displayRanks}
                                    displayRankImages={displayRankImages}
                                    renderCourse={renderCourse}
                                    withProgress={withProgress}
                                    sortableRanks={sortableRanks}
                                    disalableRanks={disalableRanks}
                                    rankControls={rankControls}
                                    newCourseComponent={newCourseComponent}
                                    onCourseMove={onCourseMove}
                                    sortableCourses={sortableCourses}
                                    coursesWrapperClassName={
                                        coursesWrapperClassName
                                    }
                                    rankImageClassname={rankImageClassname}
                                    backgroundColorTransition={
                                        backgroundColorTransition
                                    }
                                />
                            </Disclosure>
                        </div>
                    );

                    if (sortableRanks) {
                        return (
                            <SortableElement key={rankId} index={rankIndex}>
                                {rankElement}
                            </SortableElement>
                        );
                    }

                    return rankElement;
                })}
            </div>
        </div>
    );
}
